'use client';

import { Box, Stack, Typography, CircularProgress } from '@mui/material';

import TagBlock from './market-rankings-tag-block';
import SpecBlock from './market-rankings-spec-block';
import ReviewBlock from './market-rankings-review-block';
import DetailCollectBar from './market-rankings-detail-collect-bar';
import DetailMediaBlock from './market-rankings-detail-media-block';
import { foundDetail, hasCollectedDetail } from '../utils/market-rankings-product';

import type { TagSelectHandler } from './market-rankings-tag-block';
import type { MarketProduct } from '../utils/market-rankings-product';
import type { DetailExpandSignal } from './market-rankings-detail-media-block';
import type { ProductDetailState } from '../hooks/load/fetch/use-product-detail';

const UNRANKED_NOTE =
  '판매몰 검색으로 찾은 상품이라 순위·가격 추이가 없습니다. 상세페이지를 수집하면 이미지·영상과 AI 태그를 여기서 바로 볼 수 있습니다.';

/**
 * 랭킹에 없는 상품(판매몰 실시간 검색분)의 본문.
 *
 * 순위·추이 대신 **상세 수집을 걸어 주고**, 수집이 끝나면 랭킹 상품과 똑같이 태그·리뷰·
 * 고시정보·상세페이지를 보여 준다.
 */
export default function UnrankedProductPanel({
  product,
  detail,
  dense,
  sections,
  onSelectTag,
  activeTags,
  expandSignal,
  onCollected,
}: {
  product: MarketProduct;
  detail?: ProductDetailState;
  dense?: boolean;
  /** 그릴 블록. 없으면 전부 */
  sections?: string[];
  onSelectTag?: TagSelectHandler;
  activeTags?: Record<string, string>;
  expandSignal?: DetailExpandSignal;
  onCollected?: () => void | Promise<void>;
}) {
  const show = (key: string) => !sections || sections.includes(key);
  const data = foundDetail(detail?.data);
  const hasCollected = hasCollectedDetail(detail?.data);

  return (
    <Stack spacing={1.5}>
      <DetailCollectBar
        product={product}
        hasCollected={hasCollected}
        onCollected={onCollected}
        note={UNRANKED_NOTE}
      />

      {detail?.loading ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 1, color: 'text.secondary' }}>
          <CircularProgress size={16} />
          <Typography variant="caption">상세 정보를 불러오는 중…</Typography>
        </Stack>
      ) : null}

      {data ? (
        <Box>
          {show('tags') && data.hasTags ? (
            <Box sx={{ mb: 2 }}>
              <TagBlock tags={data.tags} onSelectTag={onSelectTag} activeTags={activeTags} />
            </Box>
          ) : null}
          {show('review') ? <ReviewBlock review={data.review} source={product.source} /> : null}
          {show('spec') ? <SpecBlock notice={data.notice} spec={data.spec} /> : null}
          {show('media') ? (
            <DetailMediaBlock
              media={data.media}
              textAll={data.detailTextAll}
              dense={dense}
              expandSignal={expandSignal}
              version={{
                mediaDate: data.mediaDate,
                changedAt: data.detailChangedAt,
                versionCount: data.detailVersionCount,
                // 직전 버전 대비 무엇이 달라졌나 — 버전 인덱스가 있을 때만 찬다
                current: data.detailVersion,
                // 개편 이력 — 칸을 누르면 그 시점 상세페이지를 되살린다
                timeline: data.detailVersions,
                shownHash: data.detailHash,
                ref: { postId: data.postId, source: data.source },
              }}
            />
          ) : null}
        </Box>
      ) : null}
    </Stack>
  );
}
